import { store } from '../store';
import { featureFlags } from '../config/featureFlags';
import { adminAuthenticatedFetch } from '../utils/adminAuthenticatedFetch';

const API_BASE = featureFlags.general.apiUrl;

function getAdminToken(): string {
  return store.getState().auth.token || '';
}

function withNoCache(url: string): string {
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}_ts=${Date.now()}`;
}

async function adminFetch(url: string, init: RequestInit = {}, timeoutMs = 20000): Promise<Response> {
  return adminAuthenticatedFetch(url, init, timeoutMs);
}

/** Unwraps the StandardApiResponse envelope: { status, code, message, data, errors }. */
async function unwrap<T>(res: Response, fallbackError: string): Promise<T> {
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const errors = body?.errors;
    const detail = Array.isArray(errors) && errors.length > 0 ? errors[0] : body?.message;
    throw new Error(typeof detail === 'string' && detail ? detail : fallbackError);
  }
  return body?.data as T;
}

// ===== Types =====

export interface T212Account {
  id: number;
  name: string;
  environment: 'DEMO' | 'LIVE' | string;
  isActive: boolean;
  apiKeyConfigured: boolean;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface T212AccountCreateRequest {
  name: string;
  apiKey: string;
  apiSecret?: string;
  environment: 'DEMO' | 'LIVE';
  isActive?: boolean;
}

export interface T212AccountTestResult {
  accountId: number;
  success: boolean;
  message: string;
  responseTimeMs: number;
  error?: string | null;
}

export interface StockAccountOption {
  id: number;
  name: string;
  environment: 'DEMO' | 'LIVE' | string;
}

/** Trading 212 /equity/account/cash payload. */
export interface StockCash {
  free?: number | null;
  total?: number | null;
  invested?: number | null;
  /** Unrealized P&L of open positions. */
  ppl?: number | null;
  /** Realized result. */
  result?: number | null;
  pieCash?: number | null;
  blocked?: number | null;
}

export interface StockSummary {
  cash: StockCash;
  currencyCode?: string | null;
  environment: 'DEMO' | 'LIVE' | string;
  accountName?: string | null;
}

/** Trading 212 /equity/portfolio row. */
export interface StockPosition {
  ticker: string;
  quantity: number;
  averagePrice?: number | null;
  currentPrice?: number | null;
  /** Unrealized P&L in account currency. */
  ppl?: number | null;
  fxPpl?: number | null;
  initialFillDate?: string | null;
}

/** Trading 212 /equity/history/orders item (fields vary by order type/status). */
export interface StockHistoricalOrder {
  type?: string | null;
  id?: number | null;
  fillId?: number | null;
  ticker?: string | null;
  filledQuantity?: number | null;
  orderedQuantity?: number | null;
  filledValue?: number | null;
  orderedValue?: number | null;
  limitPrice?: number | null;
  stopPrice?: number | null;
  fillPrice?: number | null;
  status?: string | null;
  dateCreated?: string | null;
  dateModified?: string | null;
  dateExecuted?: string | null;
}

export interface StockOrderHistory {
  items?: StockHistoricalOrder[];
  nextPagePath?: string | null;
}

/** Trading 212 /equity/orders row (pending, i.e. placed but unfilled). */
export interface StockPendingOrder {
  id: number;
  ticker?: string | null;
  type?: string | null;
  /** Signed: positive = buy, negative = sell. */
  quantity?: number | null;
  limitPrice?: number | null;
  stopPrice?: number | null;
  status?: string | null;
  creationTime?: string | null;
  value?: number | null;
}

/** Trading 212 /equity/metadata/instruments row. */
export interface StockInstrument {
  ticker: string;
  name?: string | null;
  shortName?: string | null;
  type?: string | null;
  currencyCode?: string | null;
  minTradeQuantity?: number | null;
  maxOpenQuantity?: number | null;
}

export interface PlaceStockOrderRequest {
  ticker: string;
  side: 'BUY' | 'SELL';
  type: 'MARKET' | 'LIMIT' | 'STOP';
  quantity: number;
  limitPrice?: number;
  stopPrice?: number;
  timeValidity?: 'DAY' | 'GOOD_TILL_CANCEL';
}

/** Local audit row for an order placed through this platform. */
export interface LocalStockOrder {
  id: number;
  t212OrderId?: number | null;
  ticker: string;
  side: 'BUY' | 'SELL' | string;
  orderType: string;
  quantity: number;
  limitPrice?: number | null;
  stopPrice?: number | null;
  timeValidity?: string | null;
  status: string;
  environment?: string | null;
  errorMessage?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface StockGrowthChallengeLadderStep {
  atUsd: number;
  lockUsd: number;
  reached?: boolean;
}

export interface StockChallengeBotSummary {
  botId: number;
  label?: string | null;
  status?: string | null;
  amount?: number | null;
  netPnl?: number | null;
  openLegs?: number;
  lastError?: string | null;
}

export interface StockGrowthChallenge {
  id: number;
  name?: string | null;
  t212ConfigId: number;
  t212AccountName?: string | null;
  startBalance: number;
  targetBalance: number;
  floorBalance: number;
  lockedProfit?: number | null;
  equity?: number | null;
  riskEquity?: number | null;
  progressPct?: number | null;
  accountEquity?: number | null;
  realPnl?: number | null;
  status: string;
  statusNote?: string | null;
  daysElapsed?: number | null;
  startedAt?: string | null;
  endedAt?: string | null;
  lastEvaluatedAt?: string | null;
  createdAt?: string | null;
  ladder?: StockGrowthChallengeLadderStep[];
  bots?: StockChallengeBotSummary[];
}

export interface CreateStockGrowthChallengeRequest {
  name?: string | null;
  t212ConfigId: number;
  startBalance: number;
  targetBalance: number;
  floorBalance: number;
  ladder?: StockGrowthChallengeLadderStep[];
  confirmationText: string;
}

export interface StockActivityBotLeg {
  id: number;
  ticker: string;
  displaySymbol?: string | null;
  quantity: number;
  entryPrice?: number | null;
  stopPrice?: number | null;
  status: string;
  exitReason?: string | null;
  exitPrice?: number | null;
  pnl?: number | null;
  openedAt?: string | null;
  closedAt?: string | null;
}

export interface StockActivityBot {
  id: number;
  referenceName?: string | null;
  t212ConfigId: number;
  t212AccountName?: string | null;
  amount: number;
  currencyCode?: string | null;
  maxOpenPositions: number;
  targetProfitPct?: number | null;
  stopLossPct?: number | null;
  continuousRun?: boolean;
  macroLongBlockEnabled?: boolean;
  universeSymbols?: string[];
  status: string;
  positionsOpened?: number;
  openLegs?: number;
  lastError?: string | null;
  lastRunAt?: string | null;
  createdAt?: string | null;
  challengeBot?: boolean;
  legs?: StockActivityBotLeg[];
}

export interface SaveStockActivityBotRequest {
  referenceName?: string | null;
  t212ConfigId: number;
  amount: number;
  currencyCode?: string | null;
  maxOpenPositions?: number;
  targetProfitPct?: number;
  stopLossPct?: number;
  continuousRun?: boolean;
  macroLongBlockEnabled?: boolean;
  universeSymbols?: string[];
}

// ===== Service =====

export const stockPortfolioService = {
  // --- Account management (My Trading Accounts screen) ---

  listT212Accounts: async (): Promise<T212Account[]> => {
    const res = await adminFetch(withNoCache(`${API_BASE}/api/admin/trading/t212-accounts`), {
      headers: { Authorization: `Bearer ${getAdminToken()}` },
    });
    const data = await unwrap<T212Account[]>(res, 'Failed to load Trading 212 accounts');
    return Array.isArray(data) ? data : [];
  },

  createT212Account: async (payload: T212AccountCreateRequest): Promise<T212Account> => {
    const res = await adminFetch(`${API_BASE}/api/admin/trading/t212-accounts`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getAdminToken()}`,
      },
      body: JSON.stringify(payload),
    });
    return unwrap<T212Account>(res, 'Failed to create Trading 212 account');
  },

  deleteT212Account: async (id: number): Promise<void> => {
    const res = await adminFetch(`${API_BASE}/api/admin/trading/t212-accounts/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${getAdminToken()}` },
    });
    await unwrap<void>(res, 'Failed to deactivate Trading 212 account');
  },

  testT212Account: async (id: number): Promise<T212AccountTestResult> => {
    const res = await adminFetch(`${API_BASE}/api/admin/trading/t212-accounts/${id}/test`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${getAdminToken()}` },
    });
    return unwrap<T212AccountTestResult>(res, 'Connection test failed');
  },

  // --- Portfolio (Stock Portfolio page) ---

  listStockAccounts: async (): Promise<StockAccountOption[]> => {
    const res = await adminFetch(withNoCache(`${API_BASE}/api/admin/stock-portfolio/accounts`), {
      headers: { Authorization: `Bearer ${getAdminToken()}` },
    });
    const data = await unwrap<StockAccountOption[]>(res, 'Failed to load stock accounts');
    return Array.isArray(data) ? data : [];
  },

  getStockSummary: async (configId: number): Promise<StockSummary> => {
    const res = await adminFetch(
      withNoCache(`${API_BASE}/api/admin/stock-portfolio/${configId}/summary`),
      { headers: { Authorization: `Bearer ${getAdminToken()}` } },
    );
    return unwrap<StockSummary>(res, 'Failed to load portfolio summary');
  },

  getStockPositions: async (configId: number): Promise<StockPosition[]> => {
    const res = await adminFetch(
      withNoCache(`${API_BASE}/api/admin/stock-portfolio/${configId}/positions`),
      { headers: { Authorization: `Bearer ${getAdminToken()}` } },
    );
    const data = await unwrap<StockPosition[]>(res, 'Failed to load positions');
    return Array.isArray(data) ? data : [];
  },

  getStockOrders: async (configId: number): Promise<StockHistoricalOrder[]> => {
    const res = await adminFetch(
      withNoCache(`${API_BASE}/api/admin/stock-portfolio/${configId}/orders`),
      { headers: { Authorization: `Bearer ${getAdminToken()}` } },
    );
    const data = await unwrap<StockOrderHistory | StockHistoricalOrder[]>(res, 'Failed to load order history');
    if (Array.isArray(data)) return data;
    return Array.isArray(data?.items) ? data.items : [];
  },

  // --- Orders (Sprint 2: manual trading) ---

  getPendingOrders: async (configId: number): Promise<StockPendingOrder[]> => {
    const res = await adminFetch(
      withNoCache(`${API_BASE}/api/admin/stock-portfolio/${configId}/pending-orders`),
      { headers: { Authorization: `Bearer ${getAdminToken()}` } },
    );
    const data = await unwrap<StockPendingOrder[]>(res, 'Failed to load pending orders');
    return Array.isArray(data) ? data : [];
  },

  /** Full tradable-instruments catalog — large payload, cached server-side. */
  getInstruments: async (configId: number): Promise<StockInstrument[]> => {
    const res = await adminFetch(
      `${API_BASE}/api/admin/stock-portfolio/${configId}/instruments`,
      { headers: { Authorization: `Bearer ${getAdminToken()}` } },
      45000,
    );
    const data = await unwrap<StockInstrument[]>(res, 'Failed to load instruments');
    return Array.isArray(data) ? data : [];
  },

  /** Price + chart for the trade drawer (Yahoo / Market Intelligence — not T212). */
  getMarketData: async (
    configId: number,
    symbol: string,
    range = '5d',
  ): Promise<{
    symbol?: string;
    price?: number | null;
    changePercent?: number | null;
    points?: Array<{ t: number; price: number }>;
    source?: string | null;
  }> => {
    const res = await adminFetch(
      withNoCache(
        `${API_BASE}/api/admin/stock-portfolio/${configId}/market-data?symbol=${encodeURIComponent(symbol)}&range=${encodeURIComponent(range)}`,
      ),
      { headers: { Authorization: `Bearer ${getAdminToken()}` } },
      30000,
    );
    return unwrap(res, 'Failed to load market data');
  },

  placeStockOrder: async (configId: number, payload: PlaceStockOrderRequest): Promise<LocalStockOrder> => {
    const res = await adminFetch(`${API_BASE}/api/admin/stock-portfolio/${configId}/orders`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getAdminToken()}`,
      },
      body: JSON.stringify(payload),
    });
    return unwrap<LocalStockOrder>(res, 'Failed to place order');
  },

  cancelStockOrder: async (configId: number, t212OrderId: number): Promise<void> => {
    const res = await adminFetch(
      `${API_BASE}/api/admin/stock-portfolio/${configId}/orders/${t212OrderId}`,
      {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${getAdminToken()}` },
      },
    );
    await unwrap<void>(res, 'Failed to cancel order');
  },

  getLocalOrders: async (configId: number): Promise<LocalStockOrder[]> => {
    const res = await adminFetch(
      withNoCache(`${API_BASE}/api/admin/stock-portfolio/${configId}/local-orders`),
      { headers: { Authorization: `Bearer ${getAdminToken()}` } },
    );
    const data = await unwrap<LocalStockOrder[]>(res, 'Failed to load platform orders');
    return Array.isArray(data) ? data : [];
  },

  // --- Stock Growth Challenge ---

  listStockGrowthChallenges: async (): Promise<StockGrowthChallenge[]> => {
    const res = await adminFetch(withNoCache(`${API_BASE}/api/admin/stock-challenge`), {
      headers: { Authorization: `Bearer ${getAdminToken()}` },
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body?.message || 'Failed to load stock challenges');
    }
    const data = await res.json();
    return Array.isArray(data) ? data as StockGrowthChallenge[] : [];
  },

  createStockGrowthChallenge: async (payload: CreateStockGrowthChallengeRequest): Promise<StockGrowthChallenge> => {
    const res = await adminFetch(`${API_BASE}/api/admin/stock-challenge`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getAdminToken()}`,
      },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body?.message || 'Failed to create stock challenge');
    }
    return res.json() as Promise<StockGrowthChallenge>;
  },

  pauseStockGrowthChallenge: async (challengeId: number): Promise<StockGrowthChallenge> => {
    const res = await adminFetch(`${API_BASE}/api/admin/stock-challenge/${challengeId}/pause`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${getAdminToken()}` },
    });
    if (!res.ok) throw new Error('Failed to pause challenge');
    return res.json() as Promise<StockGrowthChallenge>;
  },

  resumeStockGrowthChallenge: async (challengeId: number): Promise<StockGrowthChallenge> => {
    const res = await adminFetch(`${API_BASE}/api/admin/stock-challenge/${challengeId}/resume`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${getAdminToken()}` },
    });
    if (!res.ok) throw new Error('Failed to resume challenge');
    return res.json() as Promise<StockGrowthChallenge>;
  },

  stopStockGrowthChallenge: async (challengeId: number): Promise<StockGrowthChallenge> => {
    const res = await adminFetch(`${API_BASE}/api/admin/stock-challenge/${challengeId}/stop`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${getAdminToken()}` },
    });
    if (!res.ok) throw new Error('Failed to stop challenge');
    return res.json() as Promise<StockGrowthChallenge>;
  },

  evaluateStockGrowthChallenge: async (challengeId: number): Promise<StockGrowthChallenge> => {
    const res = await adminFetch(`${API_BASE}/api/admin/stock-challenge/${challengeId}/evaluate`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${getAdminToken()}` },
    });
    if (!res.ok) throw new Error('Failed to evaluate challenge');
    return res.json() as Promise<StockGrowthChallenge>;
  },

  // --- Stock activity bots ---

  listStockActivityBots: async (): Promise<StockActivityBot[]> => {
    const res = await adminFetch(withNoCache(`${API_BASE}/api/admin/stock-portfolio/activity-bots`), {
      headers: { Authorization: `Bearer ${getAdminToken()}` },
    });
    if (!res.ok) throw new Error('Failed to load stock activity bots');
    const data = await res.json();
    return Array.isArray(data) ? data as StockActivityBot[] : [];
  },

  createStockActivityBot: async (payload: SaveStockActivityBotRequest): Promise<StockActivityBot> => {
    const res = await adminFetch(`${API_BASE}/api/admin/stock-portfolio/activity-bots`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getAdminToken()}`,
      },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body?.message || 'Failed to create stock activity bot');
    }
    return res.json() as Promise<StockActivityBot>;
  },

  startStockActivityBot: async (botId: number): Promise<StockActivityBot> => {
    const res = await adminFetch(`${API_BASE}/api/admin/stock-portfolio/activity-bots/${botId}/start`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${getAdminToken()}` },
    });
    if (!res.ok) throw new Error('Failed to start bot');
    return res.json() as Promise<StockActivityBot>;
  },

  pauseStockActivityBot: async (botId: number): Promise<StockActivityBot> => {
    const res = await adminFetch(`${API_BASE}/api/admin/stock-portfolio/activity-bots/${botId}/pause`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${getAdminToken()}` },
    });
    if (!res.ok) throw new Error('Failed to pause bot');
    return res.json() as Promise<StockActivityBot>;
  },
};

export default stockPortfolioService;
